import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { merge, Subject } from 'rxjs';
import { filter, take, map, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { RootState } from 'src/app/reducers';
import { findStaticData, initializeStaticDataState } from 'src/app/actions/static-data.actions';
import { META_VIEWPORT_OTHER, STATIC_DATA_CACHE_DAYS } from 'src/app/resources/config';
import { getFetchedDate } from 'src/app/selectors/static-data.selectors';
import { Dates } from 'src/app/utils/dates';
import { Log } from 'src/app/utils/log';
import { Meta } from '@angular/platform-browser';

import * as CommonIdInformationActions from '../../../../actions/common-id/common-id-information.actions';
import { getCommonIdInformations } from 'src/app/selectors/common-id/common-id-information.selectors';
import { commonIdReportAppLaunchEvent } from '../../../../actions/common-id/common-id-event-log.actions';
import { getCookie } from 'typescript-cookie';
import { COOKIE_NAME_SSC_ID, IS_BOTTOM_TOAST_BANNER_VISIBLE } from '../../../../resources/common-id-config';
import { environment } from '../../../../../environments/environment';
import { getCommonIdSignedInUser } from '../../../../selectors/common-id/common-id-auth.selectors';
import { CommonIdUser } from '../../../../models/common-id/common-id-user';

@Component({
  selector: 'app-common-id-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss']
})
export class CommonIdMainComponent implements OnInit, OnDestroy {
  private LOG_SOURCE = this.constructor.name;

  protected readonly isBottomToastBannerVisible = IS_BOTTOM_TOAST_BANNER_VISIBLE;

  private destroy$ = new Subject<void>();

  constructor(private store: Store<RootState>, private router: Router, private route: ActivatedRoute, private meta: Meta) {}

  ngOnInit() {
    this.meta.updateTag(META_VIEWPORT_OTHER);
    this.findStaticDataIfNeeded();
    this.setUpInformations();
    this.store.dispatch(commonIdReportAppLaunchEvent());
    const sscId = getCookie(COOKIE_NAME_SSC_ID) || '';
    if (sscId) {
      gtag('set', 'user_properties', { ssc_id: sscId });
    }

    this.setUpSalesforce();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private findStaticDataIfNeeded() {
    this.store
      .select(getFetchedDate)
      .pipe(take(1))
      .subscribe(fetchedDate => {
        if (!fetchedDate) {
          Log.debug(this.LOG_SOURCE, `static data が存在していないため取得します`);
          this.store.dispatch(findStaticData());
          return;
        }
        const cacheExpired = Dates.isCachedDateExpired(fetchedDate, STATIC_DATA_CACHE_DAYS);
        if (cacheExpired) {
          Log.debug(this.LOG_SOURCE, `cache 期間を超過したため再度 static data を取得します. fetchedDate: ${fetchedDate}`);
          this.store.dispatch(initializeStaticDataState());
          this.store.dispatch(findStaticData());
          return;
        }
        Log.debug(this.LOG_SOURCE, 'static data が取得済みのため何もしません');
      });
  }

  private setUpInformations(): void {
    this.store.dispatch(CommonIdInformationActions.commonIdGetInformations());

    this.store
      .select(getCommonIdInformations)
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(result => {
        const newInformation = result.response.some(info => this.compareDate(info));
        this.store.dispatch(CommonIdInformationActions.commonIdUpdateNewInformation({ newInformation }));
      });
  }

  private setUpSalesforce() {
    // Salesforceタグ用
    this.store
      .select(getCommonIdSignedInUser)
      .pipe(
        filter<CommonIdUser>(it => it != null && it !== 'none'),
        take(1)
      )
      .subscribe(user => {
        localStorage.setItem('manabiId', user.commonId);
      });
    if (!document.getElementById('salesforce-beacon')) {
      const salesforceTag = document.createElement('script');
      salesforceTag.id = 'salesforce-beacon';
      salesforceTag.src = `//cdn.evgnet.com/beacon/v55685555553wt3ej3l3n3n3i089562657/${environment.salesforceConfig}/scripts/evergage.min.js`;
      salesforceTag.defer = true;
      document.head.appendChild(salesforceTag);
    }

    // ナビゲーション完了時にSalesforceの関数を呼び出す
    // 1) ルート遷移完了 → urlAfterRedirects（#含む）
    const onNavEnd$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects) // 例: /path?a=1#sec
    );
    // 2) フラグメント変更 → 現在のパス + #fragment に正規化
    const onHashChange$ = this.route.fragment.pipe(
      distinctUntilChanged(),
      map(f => {
        const base = this.router.url.split('#')[0]; // /path?a=1
        return f ? `${base}#${f}` : base; // /path?a=1#sec
      })
    );
    merge(onNavEnd$, onHashChange$)
      .pipe(
        distinctUntilChanged(), // 同じURLなら1回だけ
        // auditTime(0)            // さらに同tick連発を1回にまとめたい時に任意で
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const SI = (window as any).SalesforceInteractions;
        if (SI && typeof SI.reinit === 'function') {
          try {
            SI.reinit();
          } catch {}
        }
      });
  }

  private compareDate(info) {
    const lastAccessedInformation = localStorage.getItem('lastAccessedInformation');
    return lastAccessedInformation !== null ? Dates.jstDateStringFromIso(info.publishedAt) > lastAccessedInformation : true;
  }
}
